import { useEffect, useMemo, useState, useCallback } from 'react';
import { apiGet, apiPut, apiPost } from '../../../services/api';
import { uploadToPathViaBackend } from '../../../services/cmsService';
import { crearEvento } from '../../../services/eventosService';
import ConfirmModal from '../components/ConfirmModal';

const TIPOS_EVENTO = [
  { value: 'competencia', label: 'Competencia' },
  { value: 'meet', label: 'Meet' },
  { value: 'exhibicion', label: 'Exhibición' },
];
const DIVISIONES_OPCIONES = [
  { value: 'Femenino', label: 'Mujeres' },
  { value: 'Masculino', label: 'Hombres' },
];
const MODALIDADES = [
  { value: 'raw', label: 'Raw' },
  { value: 'classic_raw', label: 'Classic Raw' },
  { value: 'single_ply', label: 'Single Ply' },
  { value: 'bench', label: 'Only Bench' },
  { value: 'deadlift', label: 'Only Deadlift' },
];

/* =========================
   ESTILOS
========================= */
const styles = {
  container: { padding: '24px', maxWidth: '900px' },

  headerRow: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'flex-start',
    gap: '12px'
  },

  title: { margin: 0, fontSize: '28px', fontWeight: 700 },
  subtitle: { color: '#666', marginTop: '6px', fontSize: '14px' },

  card: {
    background: '#fff',
    borderRadius: '12px',
    boxShadow: '0 2px 10px rgba(0,0,0,0.06)',
    padding: '18px',
    marginTop: '16px'
  },

  grid: {
    display: 'grid',
    gridTemplateColumns: '1fr 1fr',
    gap: '14px'
  },

  field: { display: 'flex', flexDirection: 'column', gap: '6px' },
  label: { fontSize: '13px', color: '#555', fontWeight: 600 },

  input: {
    padding: '10px 12px',
    border: '1px solid #ddd',
    borderRadius: '8px',
    fontSize: '14px'
  },

  textarea: {
    padding: '12px',
    border: '1px solid #ddd',
    borderRadius: '8px',
    fontSize: '14px',
    minHeight: '180px',
    resize: 'vertical'
  },

  row: {
    display: 'flex',
    gap: '10px',
    alignItems: 'center',
    flexWrap: 'wrap',
    marginTop: '18px'
  },

  btnPrimary: {
    padding: '10px 16px',
    background: '#1a4d8c',
    color: '#fff',
    border: 'none',
    borderRadius: '8px',
    cursor: 'pointer',
    fontWeight: 700
  },

  btnGhost: {
    padding: '10px 16px',
    background: '#f4f6f8',
    color: '#111',
    border: '1px solid #ddd',
    borderRadius: '8px',
    cursor: 'pointer',
    fontWeight: 700
  },

  btnDanger: {
    padding: '10px 16px',
    background: '#c62828',
    color: '#fff',
    border: 'none',
    borderRadius: '8px',
    cursor: 'pointer',
    fontWeight: 700
  },

  /* TOAST */
  toast: (type) => ({
    position: 'fixed',
    bottom: '24px',
    right: '24px',
    background: type === 'error' ? '#c62828' : '#2e7d32',
    color: '#fff',
    padding: '14px 18px',
    borderRadius: '12px',
    boxShadow: '0 6px 18px rgba(0,0,0,0.25)',
    fontWeight: 700,
    fontSize: '14px',
    zIndex: 9999,
    animation: 'fadeInUp .25s ease'
  })
};

const estadoColor = {
  publicado: '#2e7d32',
  propuesto: '#ef6c00',
  cerrado: '#1565c0',
  cancelado: '#546e7a',
  en_curso: '#0277bd',
  finalizado: '#455a64',
  rechazada: '#c62828'
};

const toInputDate = (iso) => (iso ? iso.slice(0, 10) : '');

/** Asegura enviar fecha en YYYY-MM-DD para evitar malinterpretación en el backend. */
const toYYYYMMDD = (value) => {
  if (!value || typeof value !== 'string') return value;
  const s = value.trim().slice(0, 10);
  if (/^\d{4}-\d{2}-\d{2}$/.test(s)) return s;
  const ddmmyyyy = s.match(/^(\d{1,2})\/(\d{1,2})\/(\d{4})$/);
  if (ddmmyyyy) {
    const [, d, m, y] = ddmmyyyy;
    return `${y}-${m.padStart(2, '0')}-${d.padStart(2, '0')}`;
  }
  return value;
};

/* =========================
   COMPONENTE
========================= */
const EditarEventosView = ({ eventoId, onBack }) => {
  const isNew = eventoId === 'new';
  const [loading, setLoading] = useState(!isNew);
  const [saving, setSaving] = useState(false);

  const [estado, setEstado] = useState('');
  const [toast, setToast] = useState(null); // { type, message }
  const [showCerrarModal, setShowCerrarModal] = useState(false);

  const [form, setForm] = useState({
    nombre: '',
    tipo_evento: 'competencia',
    descripcion: '',
    fecha_inicio: '',
    fecha_fin: '',
    fecha_limite_inscripcion: '',
    ciudad: '',
    region: '',
    ubicacion: '',
    cupos_maximos: '',
    // costo_inscripcion: deprecado (se mantiene por compatibilidad backend)
    costo_inscripcion: '',
    incluye_tested: false,
    incluye_untested: false,
    categorias_incluidas: [],
    costos_inscripcion_detalle: {
      untested_con_club: '',
      untested_independiente: '',
      tested_con_club: '',
      tested_independiente: '',
    },
    formulario_inscripcion_tipo: 'externo', // externo | interno
    formulario_inscripcion_url: '',
    modalidad_extra_habilitada: false,
    modalidad_extra_monto: '',
    edad_extra_habilitada: false,
    edad_extra_monto: '', // se mantiene por compat (mismo valor que modalidad extra)
    divisiones: [],
    modalidad: [],
    imagen_url: '',
    destacado: false
  });
  const [eventImageFile, setEventImageFile] = useState(null);
  const [uploadingImage, setUploadingImage] = useState(false);

  const endpointGet = useMemo(() => `/api/eventos/${eventoId}`, [eventoId]);
  const endpointPut = useMemo(() => `/api/eventos/${eventoId}`, [eventoId]);
  const endpointCerrar = useMemo(() => `/api/eventos/${eventoId}/cerrar`, [eventoId]);

  const setField = (key, value) =>
    setForm(prev => ({ ...prev, [key]: value }));

  const showToast = (type, message) => {
    setToast({ type, message });
    setTimeout(() => setToast(null), 3500);
  };

  const loadEvento = useCallback(async () => {
    setLoading(true);
    try {
      const res = await apiGet(endpointGet);
      const data = await res.json();

      setEstado(data.estado);

      setForm({
        nombre: data.nombre || '',
        tipo_evento: TIPOS_EVENTO.some(t => t.value === data.tipo_evento) ? data.tipo_evento : 'competencia',
        descripcion: data.descripcion || '',
        fecha_inicio: toInputDate(data.fecha_inicio),
        fecha_fin: toInputDate(data.fecha_fin),
        fecha_limite_inscripcion: toInputDate(data.fecha_limite_inscripcion),
        ciudad: data.ciudad || '',
        region: data.region || '',
        ubicacion: data.ubicacion || '',
        cupos_maximos: data.cupos_maximos ?? '',
        costo_inscripcion: data.costo_inscripcion ?? '',
        incluye_tested: !!data.incluye_tested,
        incluye_untested: !!data.incluye_untested,
        categorias_incluidas: Array.isArray(data.categorias_incluidas) ? data.categorias_incluidas : [],
        costos_inscripcion_detalle: {
          untested_con_club: data.costos_inscripcion_detalle?.untested_con_club ?? '',
          untested_independiente: data.costos_inscripcion_detalle?.untested_independiente ?? '',
          tested_con_club: data.costos_inscripcion_detalle?.tested_con_club ?? '',
          tested_independiente: data.costos_inscripcion_detalle?.tested_independiente ?? '',
        },
        formulario_inscripcion_tipo: (data.formulario_inscripcion_tipo === 'interno' ? 'interno' : 'externo'),
        formulario_inscripcion_url: data.formulario_inscripcion_url || '',
        modalidad_extra_habilitada: !!data.modalidad_extra_habilitada,
        modalidad_extra_monto: data.modalidad_extra_monto ?? '',
        edad_extra_habilitada: !!data.edad_extra_habilitada,
        edad_extra_monto: data.edad_extra_monto ?? '',
        divisiones: Array.isArray(data.divisiones) ? data.divisiones : [],
        modalidad: Array.isArray(data.modalidad) ? data.modalidad : [],
        imagen_url: data.imagen_url || data.imagen || '',
        destacado: !!data.destacado
      });
    } catch {
      showToast('error', 'Error al cargar evento');
    } finally {
      setLoading(false);
    }
  }, [endpointGet]);

  useEffect(() => {
    if (!isNew) loadEvento();
  }, [loadEvento, isNew]);


  const handleGuardar = async () => {
    setSaving(true);
    try {
      let imagen_url = form.imagen_url;
      if (eventImageFile) {
        setUploadingImage(true);
        try {
          const filename = isNew
            ? `event_new_${Date.now()}`
            : `event_${eventoId}`;
          const { url } = await uploadToPathViaBackend(eventImageFile, 'events', filename);
          imagen_url = url;
          setEventImageFile(null);
        } finally {
          setUploadingImage(false);
        }
      }
      const payload = {
        nombre: form.nombre,
        tipo_evento: form.tipo_evento,
        descripcion: form.descripcion,
        fecha_inicio: toYYYYMMDD(form.fecha_inicio),
        fecha_fin: toYYYYMMDD(form.fecha_fin),
        fecha_limite_inscripcion: toYYYYMMDD(form.fecha_limite_inscripcion),
        ciudad: form.ciudad,
        region: form.region,
        ubicacion: form.ubicacion,
        cupos_maximos: form.cupos_maximos === '' ? null : Number(form.cupos_maximos),
        costo_inscripcion: form.costo_inscripcion === '' ? null : Number(form.costo_inscripcion),
        incluye_tested: !!form.incluye_tested,
        incluye_untested: !!form.incluye_untested,
        categorias_incluidas: Array.isArray(form.categorias_incluidas) ? form.categorias_incluidas : [],
        costos_inscripcion_detalle: {
          untested_con_club: form.costos_inscripcion_detalle?.untested_con_club === '' ? null : Number(form.costos_inscripcion_detalle?.untested_con_club),
          untested_independiente: form.costos_inscripcion_detalle?.untested_independiente === '' ? null : Number(form.costos_inscripcion_detalle?.untested_independiente),
          tested_con_club: form.costos_inscripcion_detalle?.tested_con_club === '' ? null : Number(form.costos_inscripcion_detalle?.tested_con_club),
          tested_independiente: form.costos_inscripcion_detalle?.tested_independiente === '' ? null : Number(form.costos_inscripcion_detalle?.tested_independiente),
        },
        formulario_inscripcion_tipo: form.formulario_inscripcion_tipo === 'interno' ? 'interno' : 'externo',
        formulario_inscripcion_url: form.formulario_inscripcion_tipo === 'externo' ? (form.formulario_inscripcion_url || null) : null,
        modalidad_extra_habilitada: !!form.modalidad_extra_habilitada,
        modalidad_extra_monto: form.modalidad_extra_habilitada
          ? (form.modalidad_extra_monto === '' ? 0 : Number(form.modalidad_extra_monto))
          : 0,
        edad_extra_habilitada: !!form.edad_extra_habilitada,
        // edad_extra_monto NO se envía: usa el mismo valor de modalidad_extra_monto
        divisiones: Array.isArray(form.divisiones) ? form.divisiones : [],
        modalidad: Array.isArray(form.modalidad) ? form.modalidad : [],
        imagen_url: imagen_url || undefined,
        destacado: !!form.destacado
      };

      if (isNew) {
        await crearEvento(payload);
        showToast('success', 'Evento creado correctamente.');
        setTimeout(() => onBack(), 2200);
        return;
      }

      const res = await apiPut(endpointPut, payload);
      const data = await res.json().catch(() => ({}));
      if (!res.ok || !data.ok) {
        showToast('error', data.error || 'Error al guardar cambios');
        setSaving(false);
        return;
      }

      showToast('success', 'Evento actualizado correctamente');
      await loadEvento();
    } catch (err) {
      showToast('error', err.message || 'Error al guardar cambios');
    } finally {
      setSaving(false);
    }
  };

  const handleCerrarConfirm = async () => {
    setSaving(true);
    try {
      const res = await apiPost(endpointCerrar);
      const data = await res.json().catch(() => ({}));
      if (!res.ok || !data.ok) {
        throw new Error(data.error || 'No se pudo cerrar el evento. Intenta de nuevo.');
      }
      setShowCerrarModal(false);
      showToast('success', 'Evento cerrado correctamente');
      await loadEvento();
    } catch (err) {
      showToast('error', err.message || 'No se pudo cerrar el evento. Intenta de nuevo.');
    } finally {
      setSaving(false);
    }
  };

  if (loading) return <p>Cargando evento…</p>;

  return (
    <div style={styles.container}>
      <nav style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 16, fontSize: 14, color: '#666' }} aria-label="Miga de pan">
        <button type="button" onClick={onBack} style={{ background: 'none', border: 'none', color: '#1a4d8c', cursor: 'pointer', padding: 0, fontWeight: 500 }}>Eventos</button>
        <span style={{ color: '#999' }} aria-hidden="true">›</span>
        <span style={{ color: '#333', fontWeight: 500 }}>{isNew ? 'Crear evento' : 'Editar evento'}</span>
      </nav>
      <div style={styles.headerRow}>
        <div>
          <h1 style={styles.title}>{isNew ? 'Crear evento' : 'Editar evento'}</h1>
          {!isNew && (
            <p style={styles.subtitle}>
              Estado actual:{' '}
              <strong style={{ color: estadoColor[estado] || '#666' }}>
                {estado}
              </strong>
            </p>
          )}
        </div>
        <button style={styles.btnGhost} onClick={onBack} aria-label="Volver a lista de eventos">Volver</button>
      </div>

      <div style={styles.card}>
        <div style={styles.grid}>
          <div style={styles.field}>
            <label style={styles.label}>Nombre</label>
            <input style={styles.input} value={form.nombre} onChange={e => setField('nombre', e.target.value)} />
          </div>

          <div style={styles.field}>
            <label style={styles.label}>Tipo evento</label>
            <select style={styles.input} value={form.tipo_evento || 'competencia'} onChange={e => setField('tipo_evento', e.target.value)}>
              {TIPOS_EVENTO.map(t => (
                <option key={t.value} value={t.value}>{t.label}</option>
              ))}
            </select>
          </div>

          <div style={{ ...styles.field, gridColumn: '1 / -1' }}>
            <label style={styles.label}>Descripción</label>
            <textarea style={styles.textarea} value={form.descripcion} onChange={e => setField('descripcion', e.target.value)} />
          </div>

          <div style={styles.field}>
            <label style={styles.label}>Fecha inicio</label>
            <input type="date" style={styles.input} value={form.fecha_inicio} onChange={e => setField('fecha_inicio', e.target.value)} />
          </div>

          <div style={styles.field}>
            <label style={styles.label}>Fecha fin</label>
            <input type="date" style={styles.input} value={form.fecha_fin} onChange={e => setField('fecha_fin', e.target.value)} />
          </div>

          <div style={styles.field}>
            <label style={styles.label}>Fecha límite inscripción</label>
            <input type="date" style={styles.input} value={form.fecha_limite_inscripcion} onChange={e => setField('fecha_limite_inscripcion', e.target.value)} />
          </div>

          <div style={styles.field}>
            <label style={styles.label}>Ciudad</label>
            <input style={styles.input} value={form.ciudad} onChange={e => setField('ciudad', e.target.value)} />
          </div>

          <div style={styles.field}>
            <label style={styles.label}>Región</label>
            <input style={styles.input} value={form.region} onChange={e => setField('region', e.target.value)} />
          </div>

          <div style={styles.field}>
            <label style={styles.label}>Ubicación</label>
            <input style={styles.input} value={form.ubicacion} onChange={e => setField('ubicacion', e.target.value)} />
          </div>

          <div style={styles.field}>
            <label style={styles.label}>Cupos máximos</label>
            <input type="number" style={styles.input} value={form.cupos_maximos} onChange={e => setField('cupos_maximos', e.target.value)} />
          </div>

          <div style={{ ...styles.field, gridColumn: '1 / -1' }}>
            <label style={styles.label}>Modalidad de competencia</label>
            <div style={{ display: 'flex', gap: 16, flexWrap: 'wrap' }}>
              <label style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                <input
                  type="checkbox"
                  checked={!!form.incluye_tested}
                  onChange={e => setField('incluye_tested', e.target.checked)}
                />
                TESTED
              </label>
              <label style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                <input
                  type="checkbox"
                  checked={!!form.incluye_untested}
                  onChange={e => setField('incluye_untested', e.target.checked)}
                />
                UNTESTED
              </label>
            </div>
          </div>

          <div style={{ ...styles.field, gridColumn: '1 / -1' }}>
            <label style={styles.label}>Categorías incluidas</label>
            <div style={{ display: 'flex', gap: 16, flexWrap: 'wrap' }}>
              {[
                { value: 'sub_junior', label: 'Sub junior' },
                { value: 'junior', label: 'Junior' },
                { value: 'open', label: 'Open' },
                { value: 'master', label: 'Master' },
              ].map((c) => (
                <label key={c.value} style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                  <input
                    type="checkbox"
                    checked={(form.categorias_incluidas || []).includes(c.value)}
                    onChange={() => {
                      const arr = Array.isArray(form.categorias_incluidas) ? [...form.categorias_incluidas] : [];
                      const idx = arr.indexOf(c.value);
                      const next = idx >= 0 ? arr.filter(x => x !== c.value) : [...arr, c.value];
                      setField('categorias_incluidas', next);
                    }}
                  />
                  {c.label}
                </label>
              ))}
            </div>
          </div>

          <div style={{ ...styles.field, gridColumn: '1 / -1' }}>
            <label style={styles.label}>Costos de inscripción</label>
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
              <div style={styles.field}>
                <label style={styles.label}>Untested con club</label>
                <input
                  type="number"
                  style={{ ...styles.input, opacity: form.incluye_untested ? 1 : 0.6 }}
                  disabled={!form.incluye_untested}
                  value={form.costos_inscripcion_detalle?.untested_con_club ?? ''}
                  onChange={e => setField('costos_inscripcion_detalle', { ...(form.costos_inscripcion_detalle || {}), untested_con_club: e.target.value })}
                />
              </div>
              <div style={styles.field}>
                <label style={styles.label}>Untested independiente</label>
                <input
                  type="number"
                  style={{ ...styles.input, opacity: form.incluye_untested ? 1 : 0.6 }}
                  disabled={!form.incluye_untested}
                  value={form.costos_inscripcion_detalle?.untested_independiente ?? ''}
                  onChange={e => setField('costos_inscripcion_detalle', { ...(form.costos_inscripcion_detalle || {}), untested_independiente: e.target.value })}
                />
              </div>
              <div style={styles.field}>
                <label style={styles.label}>Tested con club</label>
                <input
                  type="number"
                  style={{ ...styles.input, opacity: form.incluye_tested ? 1 : 0.6 }}
                  disabled={!form.incluye_tested}
                  value={form.costos_inscripcion_detalle?.tested_con_club ?? ''}
                  onChange={e => setField('costos_inscripcion_detalle', { ...(form.costos_inscripcion_detalle || {}), tested_con_club: e.target.value })}
                />
              </div>
              <div style={styles.field}>
                <label style={styles.label}>Tested independiente</label>
                <input
                  type="number"
                  style={{ ...styles.input, opacity: form.incluye_tested ? 1 : 0.6 }}
                  disabled={!form.incluye_tested}
                  value={form.costos_inscripcion_detalle?.tested_independiente ?? ''}
                  onChange={e => setField('costos_inscripcion_detalle', { ...(form.costos_inscripcion_detalle || {}), tested_independiente: e.target.value })}
                />
              </div>
            </div>
          </div>

          <div style={{ ...styles.field, gridColumn: '1 / -1' }}>
            <label style={styles.label}>Formulario de inscripción</label>
            <div style={{ display: 'flex', gap: 16, flexWrap: 'wrap' }}>
              <label style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                <input
                  type="radio"
                  name="formulario_inscripcion_tipo"
                  checked={form.formulario_inscripcion_tipo !== 'interno'}
                  onChange={() => setField('formulario_inscripcion_tipo', 'externo')}
                />
                Formulario externo
              </label>
              <label style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                <input
                  type="radio"
                  name="formulario_inscripcion_tipo"
                  checked={form.formulario_inscripcion_tipo === 'interno'}
                  onChange={() => setField('formulario_inscripcion_tipo', 'interno')}
                />
                Formulario interno
              </label>
            </div>
            {form.formulario_inscripcion_tipo !== 'interno' && (
              <div style={{ marginTop: 10 }}>
                <label style={styles.label}>URL del formulario externo</label>
                <input
                  style={styles.input}
                  value={form.formulario_inscripcion_url || ''}
                  onChange={e => setField('formulario_inscripcion_url', e.target.value)}
                  placeholder="https://..."
                />
              </div>
            )}
          </div>

          <div style={{ ...styles.field, gridColumn: '1 / -1' }}>
            <label style={styles.label}>Modalidad extra</label>
            <div style={{ display: 'flex', gap: 16, flexWrap: 'wrap', alignItems: 'center' }}>
              <label style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                <input
                  type="checkbox"
                  checked={!!form.modalidad_extra_habilitada}
                  onChange={e => setField('modalidad_extra_habilitada', e.target.checked)}
                />
                Existe modalidad extra
              </label>
              <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                <span style={{ ...styles.label, marginBottom: 0 }}>Monto</span>
                <input
                  type="number"
                  style={{ ...styles.input, width: 160, opacity: form.modalidad_extra_habilitada ? 1 : 0.6 }}
                  disabled={!form.modalidad_extra_habilitada}
                  value={form.modalidad_extra_monto ?? ''}
                  onChange={e => setField('modalidad_extra_monto', e.target.value)}
                  placeholder="15000"
                />
              </div>
            </div>
          </div>

          <div style={{ ...styles.field, gridColumn: '1 / -1' }}>
            <label style={styles.label}>División de edad extra</label>
            <div style={{ display: 'flex', gap: 16, flexWrap: 'wrap', alignItems: 'center' }}>
              <label style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                <input
                  type="checkbox"
                  checked={!!form.edad_extra_habilitada}
                  onChange={e => setField('edad_extra_habilitada', e.target.checked)}
                />
                Existe división de edad extra
              </label>
              <span style={{ fontSize: 13, color: '#666' }}>
                Costo: usa el mismo valor que “Modalidad extra”.
              </span>
            </div>
          </div>

          <div style={{ ...styles.field, gridColumn: '1 / -1' }}>
            <label style={styles.label}>Divisiones</label>
            <div
              style={{
                padding: '10px 12px',
                border: '1px solid #ddd',
                borderRadius: '8px',
                background: '#fff',
                boxShadow: '0 1px 3px rgba(0,0,0,0.08)',
                display: 'flex',
                flexDirection: 'column',
                gap: '8px'
              }}
            >
              {DIVISIONES_OPCIONES.map((d) => (
                <label key={d.value} style={{ display: 'flex', alignItems: 'center', gap: '8px', cursor: 'pointer', fontSize: '14px' }}>
                  <input
                    type="checkbox"
                    checked={(form.divisiones || []).includes(d.value)}
                    onChange={() => {
                      const arr = Array.isArray(form.divisiones) ? [...form.divisiones] : [];
                      const idx = arr.indexOf(d.value);
                      const next = idx >= 0 ? arr.filter((_, i) => i !== idx) : [...arr, d.value];
                      setField('divisiones', next);
                    }}
                  />
                  {d.label}
                </label>
              ))}
            </div>
          </div>

          <div style={{ ...styles.field, gridColumn: '1 / -1' }}>
            <label style={styles.label}>Modalidad (puede elegir varias)</label>
            <div style={{ display: 'flex', flexWrap: 'wrap', gap: '8px 16px' }}>
              {MODALIDADES.map((m) => (
                <label key={m.value} style={{ display: 'flex', alignItems: 'center', gap: '6px', cursor: 'pointer', fontSize: '14px' }}>
                  <input
                    type="checkbox"
                    checked={(form.modalidad || []).includes(m.value)}
                    onChange={() => {
                      const arr = Array.isArray(form.modalidad) ? [...form.modalidad] : [];
                      const idx = arr.indexOf(m.value);
                      const next = idx >= 0 ? arr.filter((_, i) => i !== idx) : [...arr, m.value];
                      setField('modalidad', next.length ? next : []);
                    }}
                  />
                  {m.label}
                </label>
              ))}
            </div>
          </div>

          <div style={{ ...styles.field, gridColumn: '1 / -1', display: 'flex', alignItems: 'center', gap: '10px' }}>
            <input
              type="checkbox"
              id="destacado"
              checked={!!form.destacado}
              onChange={e => setField('destacado', e.target.checked)}
            />
            <label htmlFor="destacado" style={{ ...styles.label, marginBottom: 0 }}>
              Evento destacado (aparece en la sección «Eventos destacados» de la home y página Eventos)
            </label>
          </div>

          <div style={{ ...styles.field, gridColumn: '1 / -1' }}>
            <label style={styles.label}>Imagen del evento</label>
            {form.imagen_url && (
              <div style={{ marginBottom: '10px' }}>
                <img src={form.imagen_url} alt="" style={{ maxWidth: 200, maxHeight: 120, objectFit: 'cover', borderRadius: '8px' }} />
                <span style={{ marginLeft: '10px', color: '#666', fontSize: '13px' }}>Actual. Sube otra para reemplazar.</span>
              </div>
            )}
            <input
              type="file"
              accept="image/jpeg,image/png,image/webp,image/gif"
              onChange={e => setEventImageFile(e.target.files?.[0] || null)}
            />
            {eventImageFile && <span style={{ marginLeft: '10px', fontSize: '13px', color: '#555' }}>Nueva imagen seleccionada (se sube al guardar)</span>}
            {uploadingImage && <span style={{ marginLeft: '10px', color: '#1a4d8c' }}>Subiendo imagen…</span>}
          </div>
        </div>

        <div style={styles.row}>
          <button style={styles.btnPrimary} onClick={handleGuardar} disabled={saving || uploadingImage}>
            {isNew ? 'Crear evento' : 'Guardar cambios'}
          </button>

          {!isNew && (
            <button
              style={styles.btnDanger}
              onClick={() => setShowCerrarModal(true)}
              disabled={saving || estado === 'cerrado'}
            >
              Cerrar evento
            </button>
          )}
        </div>
      </div>

      <ConfirmModal
        open={showCerrarModal}
        onClose={() => !saving && setShowCerrarModal(false)}
        onConfirm={handleCerrarConfirm}
        title="Cerrar evento"
        message="¿Estás seguro de que deseas cerrar este evento? No se aceptarán más inscripciones."
        confirmLabel="Sí, cerrar evento"
        cancelLabel="Cancelar"
        variant="danger"
        loading={saving}
      />

      {toast && (
        <div style={styles.toast(toast.type)}>
          {toast.message}
        </div>
      )}
    </div>
  );
};

export default EditarEventosView;
